import { useEffect, useState } from 'react';
import {
  Settings,
  Filter,
  Users,
  UsersRound,
  Tag,
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import Avatar from '../ui/Avatar';
import { toSlug } from '../../utils/slug';

const COLOR_OPTIONS = [
  { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200' },
  { bg: 'bg-sky-100', text: 'text-sky-800', border: 'border-sky-200' },
  { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
  { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-200' },
  { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
  { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
];

const ROLE_OPTIONS = [
  { id: 'admin', label: 'Administrador' },
  { id: 'agent', label: 'Atendente' },
  { id: 'viewer', label: 'Visualizador' },
];

export default function SettingsPanel({
  funnelStages,
  onSaveStage,
  onDeleteStage,
  onReorderStages,
  onToast,
  users,
  onSaveUser,
  onDeleteUser,
  teams,
  onSaveTeam,
  onDeleteTeam,
  tags,
  onSaveTag,
  onDeleteTag,
  slaConfig,
  onSaveSla,
}) {
  const [activeSection, setActiveSection] = useState('stages');
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  const SectionButton = ({ id, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => { setActiveSection(id); setEditingId(null); setIsAdding(false); }}
      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all text-left ${
        activeSection === id
          ? 'bg-appPrimary text-white shadow-lg'
          : 'text-appText hover:bg-appSurfaceBorder/30'
      }`}
    >
      <Icon size={20} />
      <span className="font-bold text-sm">{label}</span>
    </button>
  );

  return (
    <div className="flex-1 flex bg-appPage overflow-hidden">
      <div className="w-64 bg-appSurface border-r border-appSurfaceBorder flex flex-col shrink-0">
        <div className="p-6 border-b border-appSurfaceBorder">
          <h2 className="font-bold text-appText flex items-center gap-2 tracking-tight">
            <Settings size={20} className="text-appPrimary" />
            Configurações
          </h2>
        </div>
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <SectionButton id="stages" icon={Filter} label="Etapas do Funil" />
          <SectionButton id="users" icon={Users} label="Usuários" />
          <SectionButton id="teams" icon={UsersRound} label="Equipes" />
          <SectionButton id="tags" icon={Tag} label="Tags" />
          <SectionButton id="sla" icon={Clock} label="SLA & Controle" />
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-2xl">
          {activeSection === 'stages' && (
            <StagesSection
              stages={funnelStages}
              onSaveStage={onSaveStage}
              onDeleteStage={onDeleteStage}
              onReorderStages={onReorderStages}
              onToast={onToast}
              colorOptions={COLOR_OPTIONS}
              editingId={editingId}
              setEditingId={setEditingId}
              isAdding={isAdding}
              setIsAdding={setIsAdding}
            />
          )}
          {activeSection === 'users' && (
            <UsersSection
              users={users}
              teams={teams}
              onSaveUser={onSaveUser}
              onDeleteUser={onDeleteUser}
              roleOptions={ROLE_OPTIONS}
              editingId={editingId}
              setEditingId={setEditingId}
              isAdding={isAdding}
              setIsAdding={setIsAdding}
            />
          )}
          {activeSection === 'teams' && (
            <TeamsSection
              teams={teams}
              onSaveTeam={onSaveTeam}
              onDeleteTeam={onDeleteTeam}
              onToast={onToast}
              colorOptions={COLOR_OPTIONS}
              editingId={editingId}
              setEditingId={setEditingId}
              isAdding={isAdding}
              setIsAdding={setIsAdding}
            />
          )}
          {activeSection === 'tags' && (
            <TagsSection
              tags={tags}
              onSaveTag={onSaveTag}
              onDeleteTag={onDeleteTag}
              onToast={onToast}
              colorOptions={COLOR_OPTIONS}
              editingId={editingId}
              setEditingId={setEditingId}
              isAdding={isAdding}
              setIsAdding={setIsAdding}
            />
          )}
          {activeSection === 'sla' && (
            <SlaSection config={slaConfig} onUpdate={onSaveSla} />
          )}
        </div>
      </div>
    </div>
  );
}

function StagesSection({ stages, onSaveStage, onDeleteStage, onReorderStages, onToast, colorOptions, editingId, setEditingId, isAdding, setIsAdding }) {
  const defaultStage = { id: '', label: '', slug: '', color: 'bg-slate-50 border-slate-200', headerColor: 'bg-slate-100 text-slate-800' };
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [orderedStages, setOrderedStages] = useState(stages);

  useEffect(() => {
    if (!draggingId) {
      setOrderedStages(stages);
    }
  }, [stages, draggingId]);

  const handleSave = async (item) => {
    const id = item.id || item.label?.toLowerCase().replace(/\s+/g, '_') || `stage_${Date.now()}`;
    const slugRaw = item.slug || (item.label ? item.label.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-') : '');
    const normalizedSlug = toSlug(item.slug || item.label || '');
    if (!normalizedSlug) {
      onToast?.({ message: 'Informe um nome ou slug válido para a etapa.', type: 'error' });
      return;
    }
    const duplicate = stages.some((s) => {
      const otherSlug = toSlug(s.slug || s.label || '');
      return otherSlug && otherSlug === normalizedSlug && s.id !== id;
    });
    if (duplicate) {
      onToast?.({ message: 'Este slug já está em uso. Escolha outro.', type: 'error' });
      return;
    }
    const newStage = { ...item, id, slug: slugRaw };
    try {
      await onSaveStage?.(newStage, isAdding);
      if (isAdding) setIsAdding(false);
      else setEditingId(null);
    } catch {
      // Toast de erro já exibido em App.handleSaveStage; não fechar o formulário
    }
  };

  const handleDelete = async (id) => {
    await onDeleteStage?.(id);
    setEditingId(null);
  };

  const handleReorder = async () => {
    const current = [...orderedStages];
    const withPositions = current.map((s, idx) => ({ ...s, position: idx }));
    await onReorderStages?.(withPositions);
  };

  const moveItem = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    setOrderedStages((prev) => {
      const current = [...prev];
      const fromIndex = current.findIndex((s) => s.id === fromId);
      const toIndex = current.findIndex((s) => s.id === toId);
      if (fromIndex === -1 || toIndex === -1) return prev;
      const [moved] = current.splice(fromIndex, 1);
      current.splice(toIndex, 0, moved);
      return current;
    });
  };

  return (
    <SectionLayout
      title="Etapas do Funil"
      subtitle="Defina as colunas do Kanban. A ordem influencia a sequência no funil."
      items={orderedStages}
      onAdd={() => setIsAdding(true)}
      isAdding={isAdding}
      onCancelAdd={() => setIsAdding(false)}
      renderItem={(stage) => (
        <div
          key={stage.id}
          draggable={!editingId && !isAdding}
          onDragStart={(e) => {
            if (editingId || isAdding) return;
            setDraggingId(stage.id);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', stage.id);
          }}
          onDragEnd={() => {
            setDraggingId(null);
            setDragOverId(null);
          }}
          onDragOver={(e) => {
            if (editingId || isAdding) return;
            e.preventDefault();
            setDragOverId(stage.id);
            if (draggingId && draggingId !== stage.id) {
              moveItem(draggingId, stage.id);
            }
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setDragOverId(null);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOverId(null);
            handleReorder();
          }}
          className={`flex items-center gap-4 p-4 bg-appSurface rounded-xl border transition-all ${
            dragOverId === stage.id ? 'border-appPrimary ring-2 ring-appPrimary/30' : 'border-appSurfaceBorder'
          } ${draggingId === stage.id ? 'opacity-60' : ''}`}
        >
          <GripVertical size={18} className="text-appTextMuted cursor-grab" />
          <div className={`w-3 h-10 rounded-lg ${stage.headerColor}`} />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-appText">{stage.label}</p>
            <p className="text-xs text-appTextMuted">{stage.slug || stage.id}</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditingId(editingId === stage.id ? null : stage.id)}
              className="p-2 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-lg"
              aria-label="Editar"
            >
              <Pencil size={16} />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(stage.id)}
              className="p-2 text-appTextMuted hover:bg-rose-50 hover:text-rose-600 rounded-lg"
              aria-label="Excluir"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      )}
      renderForm={(item = defaultStage) => (
        <StageForm
          stage={item}
          colorOptions={colorOptions}
          onSave={handleSave}
          onCancel={() => { setEditingId(null); setIsAdding(false); }}
        />
      )}
      editingId={editingId}
    />
  );
}

function StageForm({ stage, colorOptions, onSave, onCancel }) {
  const [form, setForm] = useState(stage);

  const applyColor = (opt) => {
    const bgLight = opt.bg.replace('100', '50');
    setForm({
      ...form,
      color: `${bgLight} ${opt.border}`,
      headerColor: `${opt.bg} ${opt.text}`,
    });
  };

  return (
    <div className="p-6 bg-appSurface rounded-2xl border border-appSurfaceBorder space-y-4">
      <input
        type="text"
        value={form.label}
        onChange={(e) => setForm({ ...form, label: e.target.value })}
        placeholder="Nome da etapa (ex: Qualificação)"
        className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText font-bold focus:ring-2 focus:ring-appPrimary outline-none"
      />
      <input
        type="text"
        value={form.slug || ''}
        onChange={(e) => setForm({ ...form, slug: e.target.value })}
        placeholder="Slug (ex: qualificacao)"
        className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText font-medium focus:ring-2 focus:ring-appPrimary outline-none"
      />
      <div>
        <p className="text-xs font-bold text-appTextMuted uppercase mb-2">Cor</p>
        <div className="flex flex-wrap gap-2">
          {colorOptions.map((opt) => (
            <button
              key={opt.bg}
              type="button"
              onClick={() => applyColor(opt)}
              className={`w-8 h-8 rounded-lg ${opt.bg} border-2 ${opt.border} transition-all hover:scale-110 ${
                form.headerColor?.includes(opt.bg) ? 'ring-2 ring-appPrimary ring-offset-1' : ''
              }`}
              aria-label={opt.bg}
            />
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={() => onSave(form)}
          className="px-5 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90"
        >
          Salvar
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2.5 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-xl font-bold text-sm">
          Cancelar
        </button>
      </div>
    </div>
  );
}

function UsersSection({ users, teams, onSaveUser, onDeleteUser, roleOptions, editingId, setEditingId, isAdding, setIsAdding }) {
  const defaultUser = { id: null, name: '', teamId: null, role: 'agent', avatar: '', email: '' };

  const handleSave = async (item) => {
    const id = item.id || Date.now();
    const avatar = item.avatar || item.name?.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase() || '??';
    const newUser = { ...item, id, avatar };
    try {
      await onSaveUser?.(newUser, isAdding);
      if (isAdding) setIsAdding(false);
      else setEditingId(null);
    } catch (_) {
      // Erro já tratado em handleSaveUser (toast); formulário permanece aberto
    }
  };

  const handleDelete = async (id) => {
    await onDeleteUser?.(id);
    setEditingId(null);
  };

  const teamsArray = Array.isArray(teams) ? teams : Object.values(teams);

  return (
    <SectionLayout
      title="Usuários"
      subtitle="Gerencie os membros da equipe e suas permissões."
      items={users}
      onAdd={() => setIsAdding(true)}
      isAdding={isAdding}
      onCancelAdd={() => setIsAdding(false)}
      renderItem={(user) => (
        <div key={user.id} className="flex items-center gap-4 p-4 bg-appSurface rounded-xl border border-appSurfaceBorder">
          <Avatar label={user.avatar} size="md" color="bg-appPrimaryLight text-appPrimaryText" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-appText">{user.name}</p>
            <p className="text-xs text-appTextMuted">
              {roleOptions.find((r) => r.id === user.role)?.label || user.role} • {teamsArray.find((t) => t.id === user.teamId)?.name || 'Sem equipe'}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditingId(editingId === user.id ? null : user.id)}
              className="p-2 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-lg"
              aria-label="Editar"
            >
              <Pencil size={16} />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(user.id)}
              className="p-2 text-appTextMuted hover:bg-rose-50 hover:text-rose-600 rounded-lg"
              aria-label="Excluir"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      )}
      renderForm={(item = defaultUser) => (
        <UserForm
          user={item}
          teams={teamsArray}
          roleOptions={roleOptions}
          onSave={handleSave}
          onCancel={() => { setEditingId(null); setIsAdding(false); }}
        />
      )}
      editingId={editingId}
    />
  );
}

function UserForm({ user, teams, roleOptions, onSave, onCancel }) {
  const [form, setForm] = useState({ ...user, newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  useEffect(() => {
    setForm({ ...user, newPassword: '', confirmPassword: '' });
    setPasswordError('');
  }, [user?.id]);

  const handleSubmit = () => {
    setPasswordError('');
    if (form.newPassword || form.confirmPassword) {
      if (form.newPassword !== form.confirmPassword) {
        setPasswordError('As senhas não coincidem.');
        return;
      }
      if (form.newPassword.length < 6) {
        setPasswordError('A senha deve ter pelo menos 6 caracteres.');
        return;
      }
    }
    const toSave = { ...form };
    if (toSave.newPassword) toSave.password = toSave.newPassword;
    delete toSave.newPassword;
    delete toSave.confirmPassword;
    onSave(toSave);
  };

  const isEditing = !!user?.id;

  return (
    <div className="p-6 bg-appSurface rounded-2xl border border-appSurfaceBorder space-y-4">
      <div>
        <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Nome</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Nome completo"
          className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none"
        />
      </div>
      <div>
        <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">E-mail</label>
        <input
          type="email"
          value={form.email || ''}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="email@exemplo.com"
          className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none"
        />
      </div>
      {isEditing && (
        <>
          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Nova senha</label>
            <input
              type="password"
              value={form.newPassword}
              onChange={(e) => { setForm({ ...form, newPassword: e.target.value }); setPasswordError(''); }}
              placeholder="Deixe em branco para não alterar"
              className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Confirmar nova senha</label>
            <input
              type="password"
              value={form.confirmPassword}
              onChange={(e) => { setForm({ ...form, confirmPassword: e.target.value }); setPasswordError(''); }}
              placeholder="Repita a nova senha"
              className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none"
              autoComplete="new-password"
            />
            {passwordError && <p className="mt-1 text-sm text-rose-600">{passwordError}</p>}
          </div>
        </>
      )}
      <div>
        <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Equipe</label>
        <select
          value={form.teamId || ''}
          onChange={(e) => setForm({ ...form, teamId: e.target.value || null })}
          className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none bg-white"
        >
          <option value="">Sem equipe</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Função</label>
        <select
          value={form.role || 'agent'}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none bg-white"
        >
          {roleOptions.map((r) => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="button" onClick={handleSubmit} className="px-5 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90">
          Salvar
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2.5 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-xl font-bold text-sm">
          Cancelar
        </button>
      </div>
    </div>
  );
}

function TeamsSection({ teams, onSaveTeam, onDeleteTeam, onToast, colorOptions, editingId, setEditingId, isAdding, setIsAdding }) {
  const teamsArray = Array.isArray(teams) ? teams : Object.values(teams);
  const defaultTeam = { id: '', name: '', slug: '', color: 'bg-slate-100 text-slate-800', border: 'border-slate-200' };

  const handleSave = async (item) => {
    const id = item.id || item.name?.toLowerCase().replace(/\s+/g, '_') || `team_${Date.now()}`;
    const normalizedSlug = toSlug(item.slug || item.name || '');
    if (!normalizedSlug) {
      onToast?.({ message: 'Informe um nome ou slug válido para a equipe.', type: 'error' });
      return;
    }
    const duplicate = teamsArray.some((t) => {
      const otherSlug = toSlug(t.slug || t.name || '');
      return otherSlug && otherSlug === normalizedSlug && t.id !== id;
    });
    if (duplicate) {
      onToast?.({ message: 'Este slug já está em uso. Escolha outro.', type: 'error' });
      return;
    }
    const slugRaw = item.slug || (item.name ? toSlug(item.name) : '');
    const newTeam = { id, name: item.name, slug: slugRaw, color: item.color, border: item.border };
    try {
      await onSaveTeam?.(newTeam, isAdding);
      if (isAdding) setIsAdding(false);
      else setEditingId(null);
    } catch {
      // Toast de erro já exibido em App.handleSaveTeam; não fechar o formulário
    }
  };

  const handleDelete = async (id) => {
    await onDeleteTeam?.(id);
    setEditingId(null);
  };

  return (
    <SectionLayout
      title="Equipes"
      subtitle="Organize os atendentes em equipes para roteamento e relatórios."
      items={teamsArray}
      onAdd={() => setIsAdding(true)}
      isAdding={isAdding}
      onCancelAdd={() => setIsAdding(false)}
      renderItem={(team) => (
        <div key={team.id} className="flex items-center gap-4 p-4 bg-appSurface rounded-xl border border-appSurfaceBorder">
          <div className={`w-10 h-10 rounded-xl ${team.color} ${team.border} border flex items-center justify-center font-bold text-sm`} />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-appText">{team.name}</p>
            <p className="text-xs text-appTextMuted">{team.slug || team.id}</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditingId(editingId === team.id ? null : team.id)}
              className="p-2 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-lg"
              aria-label="Editar"
            >
              <Pencil size={16} />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(team.id)}
              className="p-2 text-appTextMuted hover:bg-rose-50 hover:text-rose-600 rounded-lg"
              aria-label="Excluir"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      )}
      renderForm={(item = defaultTeam) => (
        <TeamForm
          team={item}
          colorOptions={colorOptions}
          onSave={handleSave}
          onCancel={() => { setEditingId(null); setIsAdding(false); }}
        />
      )}
      editingId={editingId}
    />
  );
}

function TeamForm({ team, colorOptions, onSave, onCancel }) {
  const [form, setForm] = useState(team);

  const applyColor = (opt) => {
    setForm({
      ...form,
      color: `${opt.bg} ${opt.text}`,
      border: opt.border,
    });
  };

  return (
    <div className="p-6 bg-appSurface rounded-2xl border border-appSurfaceBorder space-y-4">
      <input
        type="text"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Nome da equipe (ex: Vendas)"
        className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText font-bold focus:ring-2 focus:ring-appPrimary outline-none"
      />
      <input
        type="text"
        value={form.slug ?? ''}
        onChange={(e) => setForm({ ...form, slug: e.target.value })}
        placeholder="Slug (ex: vendas)"
        className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText font-medium focus:ring-2 focus:ring-appPrimary outline-none"
      />
      <div>
        <p className="text-xs font-bold text-appTextMuted uppercase mb-2">Cor</p>
        <div className="flex flex-wrap gap-2">
          {colorOptions.map((opt) => (
            <button
              key={opt.bg}
              type="button"
              onClick={() => applyColor(opt)}
              className={`w-8 h-8 rounded-lg ${opt.bg} ${opt.border} border-2 transition-all hover:scale-110 ${
                form.color?.includes(opt.bg) ? 'ring-2 ring-appPrimary ring-offset-1' : ''
              }`}
              aria-label={opt.bg}
            />
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="button" onClick={() => onSave(form)} className="px-5 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90">
          Salvar
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2.5 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-xl font-bold text-sm">
          Cancelar
        </button>
      </div>
    </div>
  );
}

function TagsSection({ tags, onSaveTag, onDeleteTag, onToast, colorOptions, editingId, setEditingId, isAdding, setIsAdding }) {
  const defaultTag = { id: '', name: '', slug: '', color: 'bg-slate-100 text-slate-700', border: 'border-slate-200' };

  const handleSave = async (item) => {
    const id = item.id || item.name?.toLowerCase().replace(/\s+/g, '_') || `tag_${Date.now()}`;
    const normalizedSlug = toSlug(item.slug || item.name || '');
    if (!normalizedSlug) {
      onToast?.({ message: 'Informe um nome ou slug válido para a tag.', type: 'error' });
      return;
    }
    const duplicate = tags.some((t) => {
      const otherSlug = toSlug(t.slug || t.name || '');
      return otherSlug && otherSlug === normalizedSlug && t.id !== id;
    });
    if (duplicate) {
      onToast?.({ message: 'Este slug já está em uso. Escolha outro.', type: 'error' });
      return;
    }
    const slugRaw = item.slug || (item.name ? toSlug(item.name) : '');
    const newTag = { id, name: item.name, slug: slugRaw, color: item.color, border: item.border };
    try {
      await onSaveTag?.(newTag, isAdding);
      if (isAdding) setIsAdding(false);
      else setEditingId(null);
    } catch {
      // Toast de erro já exibido em App.handleSaveTag; não fechar o formulário
    }
  };

  const handleDelete = async (id) => {
    await onDeleteTag?.(id);
    setEditingId(null);
  };

  return (
    <SectionLayout
      title="Tags"
      subtitle="Crie tags para classificar e filtrar conversas."
      items={tags}
      onAdd={() => setIsAdding(true)}
      isAdding={isAdding}
      onCancelAdd={() => setIsAdding(false)}
      renderItem={(tag) => (
        <div key={tag.id} className="flex items-center gap-4 p-4 bg-appSurface rounded-xl border border-appSurfaceBorder">
          <span className={`px-3 py-1 rounded-full text-xs font-bold border ${tag.color} ${tag.border}`}>
            {tag.name}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-appTextMuted">{tag.slug || tag.id}</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditingId(editingId === tag.id ? null : tag.id)}
              className="p-2 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-lg"
              aria-label="Editar"
            >
              <Pencil size={16} />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(tag.id)}
              className="p-2 text-appTextMuted hover:bg-rose-50 hover:text-rose-600 rounded-lg"
              aria-label="Excluir"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      )}
      renderForm={(item = defaultTag) => (
        <TagForm
          tag={item}
          colorOptions={colorOptions}
          onSave={handleSave}
          onCancel={() => { setEditingId(null); setIsAdding(false); }}
        />
      )}
      editingId={editingId}
    />
  );
}

function TagForm({ tag, colorOptions, onSave, onCancel }) {
  const [form, setForm] = useState(tag);

  const applyColor = (opt) => {
    setForm({
      ...form,
      color: `${opt.bg} ${opt.text.replace('800', '700')}`,
      border: opt.border,
    });
  };

  return (
    <div className="p-6 bg-appSurface rounded-2xl border border-appSurfaceBorder space-y-4">
      <input
        type="text"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Nome da tag (ex: VIP)"
        className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText font-bold focus:ring-2 focus:ring-appPrimary outline-none"
      />
      <input
        type="text"
        value={form.slug ?? ''}
        onChange={(e) => setForm({ ...form, slug: e.target.value })}
        placeholder="Slug (ex: vip)"
        className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText font-medium focus:ring-2 focus:ring-appPrimary outline-none"
      />
      <div>
        <p className="text-xs font-bold text-appTextMuted uppercase mb-2">Cor</p>
        <div className="flex flex-wrap gap-2">
          {colorOptions.map((opt) => (
            <button
              key={opt.bg}
              type="button"
              onClick={() => applyColor(opt)}
              className={`w-8 h-8 rounded-lg ${opt.bg} ${opt.border} border-2 transition-all hover:scale-110 ${
                form.color?.includes(opt.bg) ? 'ring-2 ring-appPrimary ring-offset-1' : ''
              }`}
              aria-label={opt.bg}
            />
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="button" onClick={() => onSave(form)} className="px-5 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90">
          Salvar
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2.5 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-xl font-bold text-sm">
          Cancelar
        </button>
      </div>
    </div>
  );
}

function SlaSection({ config, onUpdate }) {
  const cfg = config || {
    firstResponseTime: { value: 15, unit: 'minutes' },
    resolutionTime: { value: 4, unit: 'hours' },
    delayAlerts: [
      { id: 'warning', percent: 80, label: 'Alerta de Atenção', enabled: true, color: 'amber' },
      { id: 'critical', percent: 100, label: 'Alerta Crítico', enabled: true, color: 'rose' },
    ],
  };

  const updateFirstResponse = (field, val) =>
    onUpdate({ ...cfg, firstResponseTime: { ...cfg.firstResponseTime, [field]: val } });
  const updateResolution = (field, val) =>
    onUpdate({ ...cfg, resolutionTime: { ...cfg.resolutionTime, [field]: val } });
  const updateAlert = (id, updates) =>
    onUpdate({
      ...cfg,
      delayAlerts: cfg.delayAlerts.map((a) => (a.id === id ? { ...a, ...updates } : a)),
    });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-appText tracking-tight">SLA & Controle</h1>
        <p className="text-sm text-appTextMuted mt-1">
          Defina metas de tempo e configure alertas de atraso para o atendimento.
        </p>
      </div>

      <div className="bg-appSurface rounded-2xl border border-appSurfaceBorder p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Clock size={20} className="text-appPrimary" />
          <h3 className="font-bold text-appText">Tempo de primeira resposta</h3>
        </div>
        <p className="text-sm text-appTextMuted">
          Tempo máximo para enviar a primeira resposta ao cliente.
        </p>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Valor</label>
            <input
              type="number"
              min={1}
              value={cfg.firstResponseTime?.value ?? 15}
              onChange={(e) => updateFirstResponse('value', Number(e.target.value) || 1)}
              className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none"
            />
          </div>
          <div className="w-36">
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Unidade</label>
            <select
              value={cfg.firstResponseTime?.unit ?? 'minutes'}
              onChange={(e) => updateFirstResponse('unit', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none bg-white"
            >
              <option value="minutes">Minutos</option>
              <option value="hours">Horas</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-appSurface rounded-2xl border border-appSurfaceBorder p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Clock size={20} className="text-appPrimary" />
          <h3 className="font-bold text-appText">Tempo de resolução</h3>
        </div>
        <p className="text-sm text-appTextMuted">
          Tempo máximo para resolver o atendimento.
        </p>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Valor</label>
            <input
              type="number"
              min={1}
              value={cfg.resolutionTime?.value ?? 4}
              onChange={(e) => updateResolution('value', Number(e.target.value) || 1)}
              className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none"
            />
          </div>
          <div className="w-36">
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Unidade</label>
            <select
              value={cfg.resolutionTime?.unit ?? 'hours'}
              onChange={(e) => updateResolution('unit', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none bg-white"
            >
              <option value="minutes">Minutos</option>
              <option value="hours">Horas</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-appSurface rounded-2xl border border-appSurfaceBorder p-6 space-y-6">
        <div className="flex items-center gap-2">
          <AlertTriangle size={20} className="text-appPrimary" />
          <h3 className="font-bold text-appText">Alertas de atraso</h3>
        </div>
        <p className="text-sm text-appTextMuted">
          Configure em que percentual do SLA os alertas devem ser disparados.
        </p>
        <div className="space-y-4">
          {(cfg.delayAlerts || []).map((alert) => (
            <div
              key={alert.id}
              className="flex items-center gap-4 p-4 rounded-xl border border-appSurfaceBorder bg-appPage/50"
            >
              <label className="flex items-center gap-3 cursor-pointer flex-1">
                <input
                  type="checkbox"
                  checked={alert.enabled ?? true}
                  onChange={(e) => updateAlert(alert.id, { enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-appSurfaceBorder text-appPrimary focus:ring-appPrimary"
                />
                <span className="font-medium text-appText">{alert.label}</span>
              </label>
              <div className="flex items-center gap-2 w-32">
                <input
                  type="number"
                  min={0}
                  max={200}
                  value={alert.percent ?? 80}
                  onChange={(e) => updateAlert(alert.id, { percent: Number(e.target.value) })}
                  className="w-16 px-3 py-2 rounded-lg border border-appSurfaceBorder text-sm text-appText text-right focus:ring-2 focus:ring-appPrimary outline-none"
                />
                <span className="text-sm text-appTextMuted">%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionLayout({
  title,
  subtitle,
  items,
  onAdd,
  isAdding,
  onCancelAdd,
  renderItem,
  renderForm,
  editingId,
}) {
  const editingItem = items.find((i) => i.id === editingId);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-appText tracking-tight">{title}</h1>
          <p className="text-sm text-appTextMuted mt-1">{subtitle}</p>
        </div>
        {!isAdding && !editingId && (
          <button
            type="button"
            onClick={onAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90 shadow-lg"
          >
            <Plus size={18} />
            Adicionar
          </button>
        )}
      </div>

      {(isAdding || editingId) && (
        <div key={editingId ?? 'new'}>
          {renderForm(editingItem)}
        </div>
      )}

      <div className="space-y-3">
        {items.map(renderItem)}
      </div>

      {items.length === 0 && !isAdding && !editingId && (
        <div className="py-16 text-center bg-appSurface rounded-2xl border-2 border-dashed border-appSurfaceBorder">
          <p className="text-appTextMuted font-medium">Nenhum item cadastrado</p>
          <button
            type="button"
            onClick={onAdd}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90"
          >
            <Plus size={18} />
            Adicionar o primeiro
          </button>
        </div>
      )}
    </div>
  );
}
